<script setup lang="ts">
import CoreLoader from "@/components/core/core-loader.vue";
import CoreTimer from "@/components/core/core-timer.vue";
import PartNotificationMessage from '@parts/part-notification-message.vue';

import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { SocialProvider, UserConnectNotification} from "@store/api/interfaces";
import { ApiError, ApiResponse } from "@/composables/useApi";

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const loading = ref<boolean>(false);
const notificationMessage = ref<UserConnectNotification>({});
const showAutoClose = ref(false);
const showReturnButton = ref(false);
const autoCloseDuration = 5;
const miniAppLink = `${import.meta.env.VITE_BOT_LINK}?startapp=account`;

const onAutoCloseEnd = () => {
	window.close();
	// If window.close() didn't work (not opened via JS), show return button
	setTimeout(() => {
		showReturnButton.value = true;
	}, 500);
};

const connectCallbackHandler = async (provider: SocialProvider) => {
	if (loading.value) {
		return;
	}

	loading.value = true;

	const notification: UserConnectNotification = {
		provider,
		status: 'success',
	};

	try {
		connectByURL();
	} catch (error) {
		console.error('ErrorProvideAuth', error);

		notification.status = 'failed';
		if (error instanceof ApiError) {
			notification.code = error.code;
		}
	} finally {
		notificationMessage.value = notification;
		showAutoClose.value = true;
		loading.value = false;

		// Send result back to opener window (delivered only to our own origin)
		if (window.opener) {
			window.opener.postMessage({
				type: 'connect-result',
				notification,
			}, window.location.origin);
		}
	}
};

const connectByURL = () => {
	const params = Object.fromEntries(
		new URLSearchParams(window.location.search)
	);
	const response:ApiResponse = {
		success: params.success === 'true' || params.success === '1',
		data: undefined,
		message: params.message ?? undefined,
		code: params.code ?? undefined,
	}
	if (!response.success) {
		throw new ApiError(response);
	}
};

onMounted(async () => {
	const callback = route.params.callback;
	if (typeof callback === 'string') {
		await connectCallbackHandler(callback as SocialProvider);
	} else {
		router.push({ name: "Account" });
	}
});

</script>

<template>
	<main class="page page-login">
		<div class="page-login__info">
			<core-loader
				v-if="loading"
			/>
			<part-notification-message
				v-else
				:notification="notificationMessage"
			>
				<div v-if="showAutoClose" class="page-connect__auto-close">
					<template v-if="!showReturnButton">
						{{ t('connect.auto_close') }}
						<core-timer
							:duration="autoCloseDuration"
							@end="onAutoCloseEnd"
							class="page-connect__timer"
						/>
					</template>
					<a
						v-if="showReturnButton"
						:href="miniAppLink"
						class="page-connect__return-btn"
					>
						{{ t('connect.return_to_miniapp') }}
					</a>
				</div>
			</part-notification-message>
		</div>
	</main>
</template>

<style lang="scss">
.page-login {
	&__info {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
	}
}
.page-connect {
	&__auto-close {
		margin-top: 2rem;
		color: rgba(var(--color-text-rgb), 0.5);
		text-align: center;
		font-size: 1.4rem;
	}
	&__timer {
		display: inline;
		font-weight: 500;
	}
	&__return-btn {
		display: inline-block;
		margin-top: 1.6rem;
		padding: 1.2rem 2.4rem;
		border-radius: 1.6rem;
		background-color: var(--color-purple);
		color: var(--color-white);
		font-size: 1.6rem;
		font-weight: 500;
		text-decoration: none;
		text-align: center;
	}
}
</style>
